import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, Move3d, RotateCcw, Search } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { PlaceInRoomDialog, PlaceInRoomResult, RoomPlacement } from './PlaceInRoomDialog';
import { RawRoomScene } from './room-scene';

/** A furniture product that can be placed — the fields the placement needs. */
export interface PlaceableProduct {
    uuid: string;
    name: string;
    thumbnailPath?: string;
    originalImagePath?: string;
    modelPath?: string;
    category?: string;
    largestDimensionM?: number;
}

/** The room analysis calls, passed down from the workspace's API hook. */
export interface RoomApi {
    analyze: (imageUrl: string) => Promise<{ jobId: string; spaceId: string }>;
    status: (jobId: string) => Promise<{ status: string; progress?: number; error?: string; message?: string }>;
    scene: (spaceId: string) => Promise<RawRoomScene>;
}

interface PlaceInRoomSectionProps {
    inputs: Record<string, any>;
    onInputChange: (key: string, value: any) => void;
    /** The room: the image this recipe was chained off. */
    roomUrl?: string;
    products: PlaceableProduct[];
    roomApi?: RoomApi;
    onUpload?: (file: File) => Promise<string>;
}

const POLL_MS = 3000;
const RUG = /\b(rug|carpet|mat|runner|doormat|kilim|dhurrie)s?\b/i;

/**
 * Place in a room — chained off an image, which is the room.
 *
 * The card analyses the room on its own as soon as it exists (floor, depth and
 * camera: the Space the visualizer places furniture on), the vendor picks the
 * piece, and the preview opens the placement. What the job records is the same
 * as for every other chained recipe — the image is the parent — plus the piece
 * that was placed, as its product.
 */
export const PlaceInRoomSection: React.FC<PlaceInRoomSectionProps> = ({
    inputs,
    onInputChange,
    roomUrl,
    products,
    roomApi,
    onUpload,
}) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [progress, setProgress] = useState<number | undefined>(undefined);
    const [query, setQuery] = useState('');
    const spaceId: string | undefined = inputs.roomSpaceId;
    const jobId: string | undefined = inputs.roomJobId;
    const analysis: 'running' | 'ready' | 'failed' | undefined = inputs.roomAnalysis;
    const compositeUrl: string | undefined = inputs.compositeUrl;
    const chosen: PlaceableProduct | undefined = inputs.placeProduct;

    // ── The room's analysis — started once, followed until it lands ─────
    const startAnalysis = async () => {
        if (!roomApi || !roomUrl) return;
        onInputChange('roomAnalysis', 'running');
        onInputChange('roomAnalysisError', undefined);
        try {
            const r = await roomApi.analyze(roomUrl);
            onInputChange('roomJobId', r.jobId);
            onInputChange('roomSpaceId', r.spaceId);
        } catch (err: any) {
            onInputChange('roomAnalysis', 'failed');
            onInputChange('roomAnalysisError', err?.message);
        }
    };
    useEffect(() => {
        if (roomUrl && roomApi && !analysis && !spaceId) void startAnalysis();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roomUrl, !!roomApi]);
    useEffect(() => {
        if (analysis !== 'running' || !jobId || !roomApi) return;
        let cancelled = false;
        const tick = async () => {
            try {
                const s = await roomApi.status(jobId);
                if (cancelled) return;
                setProgress(s.progress);
                if (s.status === 'completed') onInputChange('roomAnalysis', 'ready');
                else if (s.status === 'failed') {
                    onInputChange('roomAnalysis', 'failed');
                    onInputChange('roomAnalysisError', s.error || s.message);
                }
            } catch {
                // A dropped poll is retried on the next tick.
            }
        };
        void tick();
        const id = setInterval(tick, POLL_MS);
        return () => { cancelled = true; clearInterval(id); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analysis, jobId]);

    // ── The piece ────────────────────────────────────────────────────────
    const choose = (p: PlaceableProduct) => {
        if (p.uuid === chosen?.uuid) return;
        onInputChange('placeProduct', {
            uuid: p.uuid,
            name: p.name,
            thumbnailPath: p.thumbnailPath || p.originalImagePath,
            modelPath: p.modelPath,
            category: p.category,
            largestDimensionM: p.largestDimensionM,
        });
        // A pose belongs to the piece it was made with.
        onInputChange('placement', undefined);
        onInputChange('compositeUrl', undefined);
        onInputChange('rugMaskUrl', undefined);
    };
    const visible = useMemo(() => {
        const q = query.trim().toLowerCase();
        const list = q ? products.filter((p) => `${p.name} ${p.category || ''}`.toLowerCase().includes(q)) : products;
        return list.slice(0, 24);
    }, [products, query]);

    const onValidated = (r: PlaceInRoomResult) => {
        onInputChange('compositeUrl', r.compositeUrl);
        onInputChange('rugMaskUrl', r.rugMaskUrl);
        onInputChange('placement', r.placement);
    };

    const isRug = !!chosen && RUG.test(`${chosen.category || ''} ${chosen.name || ''}`);
    const canPlace = analysis === 'ready' && !!spaceId && !!chosen && !!roomApi && !!onUpload;
    const preview = compositeUrl || roomUrl;

    return (
        // No mouse-down guard here: the node shell already leaves buttons and
        // inputs alone, and anywhere else on the card should drag the node.
        <div className="space-y-3 px-4 py-3">
            {/* The preview — the room, or the room with the piece once placed. */}
            <div
                className="relative aspect-[4/3] w-full overflow-hidden rounded-lg border bg-muted bg-contain bg-center bg-no-repeat"
                style={preview ? { backgroundImage: `url(${preview})` } : undefined}
            >
                <div className="absolute inset-x-0 bottom-3 flex justify-center">
                    {analysis === 'running' ? (
                        <span className="flex items-center gap-1.5 rounded-md bg-background/90 px-2.5 py-1.5 text-xs text-muted-foreground">
                            <Loader2 className="size-3.5 animate-spin" />
                            {t('CONTENT_STUDIO.PLACE.ANALYSING', 'Analysing the room — floor, depth and camera…')}
                            {typeof progress === 'number' && <span className="tabular-nums">{Math.round(progress)}%</span>}
                        </span>
                    ) : analysis === 'failed' ? (
                        <span className="flex items-center gap-1.5 rounded-md bg-background/90 px-2.5 py-1 text-xs text-destructive">
                            {t('CONTENT_STUDIO.PLACE.ANALYSIS_FAILED', 'The room could not be analysed.')}
                            <Button variant="ghost" size="sm" className="h-6 gap-1 px-1.5 text-xs" onClick={() => void startAnalysis()}>
                                <RotateCcw className="size-3" />
                                {t('CONTENT_STUDIO.RETRY', 'Retry')}
                            </Button>
                        </span>
                    ) : (
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-8 gap-1.5 bg-background/95"
                            disabled={!canPlace}
                            onClick={() => setOpen(true)}
                        >
                            <Move3d className="size-4" />
                            {!chosen
                                ? t('CONTENT_STUDIO.PLACE.CHOOSE_FIRST', 'Choose a piece below')
                                : compositeUrl
                                    ? t('CONTENT_STUDIO.PLACE.ADJUST', 'Adjust placement')
                                    : t('CONTENT_STUDIO.PLACE.OPEN', 'Place the product')}
                        </Button>
                    )}
                </div>
            </div>

            {/* The piece to place. */}
            <div>
                <p className="text-xs font-medium text-muted-foreground">
                    {t('CONTENT_STUDIO.PLACE.PRODUCT_LABEL', 'Piece to place')}
                </p>
                {products.length === 0 ? (
                    <p className="mt-1.5 rounded-lg border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">
                        {t('CONTENT_STUDIO.PLACE.NO_FURNITURE', 'No furniture with a 3D model in your catalogue yet.')}
                    </p>
                ) : (
                    <>
                        <div className="relative mt-1.5">
                            <Search className="pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={t('CONTENT_STUDIO.SEARCH_PRODUCTS', 'Search by name or SKU…') as string}
                                className="h-8 ps-8 text-xs"
                            />
                        </div>
                        <div className="mt-1.5 grid max-h-[176px] grid-cols-4 gap-1.5 overflow-y-auto p-0.5">
                            {visible.map((p) => {
                                const selected = p.uuid === chosen?.uuid;
                                const thumb = p.thumbnailPath || p.originalImagePath;
                                return (
                                    <button
                                        key={p.uuid}
                                        type="button"
                                        title={p.name}
                                        onClick={() => choose(p)}
                                        className={cn(
                                            'relative aspect-square overflow-hidden rounded-md border-[1.5px] bg-muted bg-cover bg-center transition-colors',
                                            selected ? 'border-foreground' : 'border-transparent hover:border-border',
                                        )}
                                        style={thumb ? { backgroundImage: `url(${thumb})` } : undefined}
                                        aria-pressed={selected}
                                    >
                                        {selected && (
                                            <span className="absolute end-1 top-1 flex size-4 items-center justify-center rounded-full bg-foreground text-background">
                                                <Check className="size-2.5" />
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                        {chosen && (
                            <p className="mt-1.5 truncate text-xs text-foreground">{chosen.name}</p>
                        )}
                    </>
                )}
            </div>

            {open && spaceId && roomApi && onUpload && chosen && (
                <PlaceInRoomDialog
                    open={open}
                    onOpenChange={setOpen}
                    spaceId={spaceId}
                    loadScene={roomApi.scene}
                    productUuid={chosen.uuid}
                    modelPath={chosen.modelPath}
                    isRug={isRug}
                    largestDimensionM={chosen.largestDimensionM}
                    placement={inputs.placement as RoomPlacement | undefined}
                    onUpload={onUpload}
                    onValidated={onValidated}
                />
            )}
        </div>
    );
};
